import React, { useEffect } from "react";
import {
    FluentProvider,
    webLightTheme,
    Button,
    Title1,
    Card,
    makeStyles,
    tokens,
    Spinner,
    Text,
} from "@fluentui/react-components";
import { NumbersDisplay } from "./components/NumbersDisplay";
import { ExpressionInput } from "./components/ExpressionInput";
import { MobileKeyboard } from "./components/MobileKeyboard";
import { HelpDialog } from "./components/HelpDialog";
import { DifficultyTabs } from "./components/DifficultyTabs";
import { StreakDialog } from "./components/StreakDialog";
import { useGameLogic } from "./hooks/useGameLogic";

const useStyles = makeStyles({
    container: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        backgroundColor: tokens.colorNeutralBackground2,
        padding: "0px 20px",
        "@media (max-width: 768px)": {
            padding: "0",
            height: "100%",
            width: "100vw",
            overflow: "hidden",
        },
    },
    card: {
        maxWidth: "500px",
        width: "100%",
        padding: "32px",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        position: "relative",
        "@media (max-width: 768px)": {
            width: "100vw",
            height: "100%",
            maxWidth: "100vw",
            margin: "0",
            padding: "16px",
            borderRadius: "0",
            boxSizing: "border-box",
            overflow: "auto",
        },
    },
    cardHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
    },
    helpButton: {
        position: "absolute",
        width: "40px",
        height: "40px",
        fontSize: "20px",
        minWidth: "40px",
        zIndex: 10,
        top: "16px",
        right: "16px",
    },
    errorText: {
        color: tokens.colorPaletteRedForeground1,
        textAlign: "center",
    },
    loadingContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "20px",
    },
    tabsContainer: {
        marginTop: "auto",
    },
});

function NumbersGame() {
    const styles = useStyles();
    const {
        numbers,
        expression,
        numberCount,
        liveResult,
        showHelp,
        setShowHelp,
        inputRef,
        availableNumbers,
        handleExpressionChange,
        handleKeyPress,
        handleNumberCountChange,
        celebrationTimerRef,
        highlightedNumbers,
        showSuccessResult,
        loading,
        error,
        completionStatus,
        isCompleted,
        showStreakDialog,
        setShowStreakDialog,
        currentStreak,
    } = useGameLogic();

    useEffect(() => {
        return () => {
            if (celebrationTimerRef.current) {
                // eslint-disable-next-line react-hooks/exhaustive-deps
                clearTimeout(celebrationTimerRef.current);
            }
        };
    }, [celebrationTimerRef]);

    return (
        <div className={styles.container}>
            <Card className={styles.card}>
                <Button
                    className={styles.helpButton}
                    appearance="subtle"
                    size="medium"
                    onClick={() => setShowHelp(true)}
                >
                    ?
                </Button>

                <div className={styles.cardHeader}>
                    <Title1 align="center" italic>
                        equals10
                    </Title1>
                </div>

                {loading ? (
                    <div className={styles.loadingContainer}>
                        <Spinner label="Loading numbers..." />
                    </div>
                ) : error ? (
                    <Text className={styles.errorText}>{error}</Text>
                ) : (
                    <>
                        <NumbersDisplay
                            numbers={numbers}
                            highlightedIndices={highlightedNumbers}
                        />

                        <ExpressionInput
                            expression={expression}
                            onChange={handleExpressionChange}
                            inputRef={inputRef}
                            liveResult={liveResult}
                            showSuccessResult={showSuccessResult}
                            isCompleted={isCompleted}
                        />

                        <MobileKeyboard
                            onKeyPress={handleKeyPress}
                            availableNumbers={availableNumbers}
                            isCompleted={isCompleted}
                        />
                    </>
                )}

                <div className={styles.tabsContainer}>
                    <DifficultyTabs
                        numberCount={numberCount}
                        completionStatus={completionStatus}
                        isCompleted={isCompleted}
                        onNumberCountChange={handleNumberCountChange}
                    />
                </div>

                <HelpDialog
                    open={showHelp}
                    onClose={() => setShowHelp(false)}
                />
                <StreakDialog
                    open={showStreakDialog}
                    onClose={() => setShowStreakDialog(false)}
                    streak={currentStreak}
                />
            </Card>
        </div>
    );
}

function App() {
    return (
        <FluentProvider theme={webLightTheme}>
            <NumbersGame />
        </FluentProvider>
    );
}

export default App;
